import get from "lodash/get";
import includes from "lodash/includes";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import map from "lodash/map";
import some from "lodash/some";
import {
  HOOPS_STACK_MARKERS,
  IGNORED_EXCEPTION_MESSAGES,
  isHoopsError,
  isWebGlInitFailure,
} from "./src/sharedErrorConstants";

const IGNORED_NETWORK_ERRORS = ["ERR_NETWORK", "ERR_CANCELED", "ECONNABORTED"];
const IGNORED_FETCH_ERROR_MESSAGES = ["Network request failed"];

/**
 * Scrub CP-283 external-share credentials out of anything Sentry ships.
 *
 * The share URL path segment IS the credential (`/share/<token>/...`), and the byte URLs carry
 * one-shot HMAC tickets (`?ticket=...`). Sentry attaches `event.request.url`, breadcrumb URLs
 * (navigation + xhr/fetch), transaction names and span descriptions automatically, so without scrubbing every captured
 * error on a recipient page uploads a replayable link to a third-party SaaS — exactly what PRD 5's
 * "keep the URL out of any log" forbids.
 */
const SHARE_TOKEN_PATTERN = /\/share\/[^/?#\s"']+/g;
const SHARE_TICKET_PATTERN = /([?&]ticket=)[^&#\s"']+/g;

function scrubShareSecrets(value) {
  if (typeof value !== "string") return value;
  return value.replace(SHARE_TOKEN_PATTERN, "/share/[redacted]").replace(SHARE_TICKET_PATTERN, "$1[redacted]");
}

/**
 * Every string anywhere in the event rather than a list of known fields: transactions and spans carry
 * the path in names, descriptions and attributes, breadcrumbs under `data` keys that vary by type, and
 * a field list is how transaction events came to ship the token unscrubbed. `sdkProcessingMetadata`
 * is skipped: it holds the SDK's live scopes, is never sent, and is not ours to rewrite.
 */
export function scrubShareEvent(event) {
  const seen = new WeakSet();
  const walk = (node) => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    for (const key of Object.keys(node)) {
      if (key === "sdkProcessingMetadata") continue;
      const value = node[key];
      if (typeof value === "string") {
        const scrubbed = scrubShareSecrets(value);
        if (scrubbed !== value) node[key] = scrubbed;
      } else {
        walk(value);
      }
    }
  };
  walk(event);
  return event;
}

export function sentryBeforeSend(event, hint) {
  scrubShareEvent(event);

  const networkError = get(hint, "originalException.code");
  const exceptionMessage = get(hint, "originalException.message") || get(hint, "originalException.value");
  const isFetchNetworkError =
    get(hint, "originalException.name") === "TypeError" && IGNORED_FETCH_ERROR_MESSAGES.includes(exceptionMessage);
  if (
    IGNORED_NETWORK_ERRORS.includes(networkError) ||
    isFetchNetworkError ||
    get(hint, "originalException.status") == "401" ||
    (get(hint, "originalException.status") == "403" && get(hint, "originalException.response.data.errorCode") === 1001)
  ) {
    return null;
  }

  const exceptions = get(event, "exception.values", []);
  const candidateMessages = [exceptionMessage, get(event, "message"), ...map(exceptions, (ex) => get(ex, "value"))];
  const isIgnoredMessage = some(
    candidateMessages,
    (message) => isString(message) && some(IGNORED_EXCEPTION_MESSAGES, (ignored) => message.includes(ignored)),
  );
  if (isIgnoredMessage) {
    return null;
  }

  const originalException = get(hint, "originalException");
  if (isObject(originalException) && Object.hasOwn(originalException, "scFunction")) {
    return null;
  }
  if (isWebGlInitFailure(originalException)) {
    return null;
  }
  const hasHoopsFrame = some(exceptions, (ex) =>
    some(get(ex, "stacktrace.frames", []), (frame) =>
      some(HOOPS_STACK_MARKERS, (marker) => includes(get(frame, "filename"), marker)),
    ),
  );
  if ((hasHoopsFrame || isHoopsError(originalException)) && !globalThis.location.href.includes("designviewer")) {
    return null;
  }

  const isFabricSchedulerCrash = some(
    exceptions,
    (ex) =>
      get(ex, "type") === "EXC_BAD_ACCESS" &&
      some(
        get(ex, "stacktrace.frames", []),
        (frame) =>
          frame && frame.filename === "Scheduler.cpp" && includes(frame.function, "uiManagerDidFinishTransaction"),
      ),
  );
  if (isFabricSchedulerCrash) {
    return null;
  }

  return event;
}
